import { ENTERPRISE_URL } from '../lib/scenarioNav';
import { DEFAULT_STAFF_PATH, STAFF_ORGANISATIONS_PATH } from './staffNav';

// Cloudhouse-staff permissions consumed in Hub. Every other authority is org-scoped
// (org-admin:*). UX gate only — the API's RequirePermission is the real control.
export const CATALOGUE_READ = 'cloudhouse-admin:catalogue-read';

// Any staff permission opens the shell: a grants-only operator (entitlement-write, no
// catalogue-read) must still reach /staff/organisations, and so must an SSO-only operator.
export function canAccessStaff(permissions: readonly string[] | null | undefined): boolean {
  const held = permissions ?? [];
  return held.includes(CATALOGUE_READ) || held.includes(ENTITLEMENT_WRITE) || held.includes(SSO_WRITE);
}

export type StaffAuthSnapshot = { isLoading: boolean; permissions: readonly string[] | null | undefined };
export type StaffRouteDecision = { kind: 'loading' } | { kind: 'render' } | { kind: 'redirect'; to: string };

// Pure. loading while the claim is unsettled — otherwise a legit staff user could be
// hard-redirected before their permission arrives. Non-staff go back to the customer hub
// (hidden, not a 403 page — the staff surface is not advertised).
export function resolveStaffRoute(auth: StaffAuthSnapshot): StaffRouteDecision {
  if (auth.isLoading) return { kind: 'loading' };
  return canAccessStaff(auth.permissions) ? { kind: 'render' } : { kind: 'redirect', to: ENTERPRISE_URL };
}

export function canReadCatalogue(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).includes(CATALOGUE_READ);
}

// Where the staff shell's index lands: the catalogue for catalogue staff, otherwise the
// organisations page — a grants-only operator must not be dropped on a catalogue 403.
export function staffLandingPath(permissions: readonly string[] | null | undefined): string {
  return canReadCatalogue(permissions) ? DEFAULT_STAFF_PATH : STAFF_ORGANISATIONS_PATH;
}

export const CATALOGUE_WRITE = 'cloudhouse-admin:catalogue-write';

export function canEditCatalogue(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).includes(CATALOGUE_WRITE);
}

export const ENTITLEMENT_WRITE = 'cloudhouse-admin:entitlement-write';

// Gates the organisations page's grant/revoke actions; the admin reads behind the page are
// gated on this same slug server-side (their only audience is a granting operator).
export function canEditEntitlements(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).includes(ENTITLEMENT_WRITE);
}

export const ORGANISATION_DELETE = 'cloudhouse-admin:organisation-delete';

// Gates the organisations page's delete action. Distinct from ENTITLEMENT_WRITE — a grants
// operator must not see a delete button that would just 403.
export function canDeleteOrganisation(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).includes(ORGANISATION_DELETE);
}

export const SSO_WRITE = 'cloudhouse-admin:sso-write';

// Gates the organisations page's "Generate SSO setup link" action. Its own slug, not
// ENTITLEMENT_WRITE: minting a link into a customer's identity configuration is a different
// authority from granting them a tier, and canAccessStaff admits it on its own.
export function canManageSso(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).includes(SSO_WRITE);
}
